import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Clock, User, Calendar, RefreshCw, X } from 'lucide-react';
import { LoadingSpinner, ErrorMessage } from '../../UI';
import { attendanceAPI, userAPI } from '../../../services';

const EmployeeLocationTracker = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  // Load Google Maps script
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        // TODO: Replace with your actual Google Maps API key
        // Get your API key from: https://console.cloud.google.com/google/maps-apis
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    loadGoogleMaps().catch(err => {
      console.error('Failed to load Google Maps:', err);
      setError('Failed to load Google Maps. Please check your API key.');
    });
  }, []);

  // Fetch employees
  useEffect(() => {
    fetchEmployees();
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh && selectedEmployee && selectedDate === new Date().toISOString().split('T')[0]) {
      refreshIntervalRef.current = setInterval(() => {
        fetchAttendance(selectedEmployee, selectedDate);
      }, 30000); // Refresh every 30 seconds

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, selectedEmployee, selectedDate]);

  const fetchEmployees = async () => {
    try {
      const response = await userAPI.getUsers();
      if (response.status === 'success') {
        setEmployees(response.data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchAttendance = async (employeeId, date) => {
    if (!employeeId || !date) return;

    setLoading(true);
    setError('');

    try {
      const response = await attendanceAPI.getAttendanceByDate(employeeId, date);
      if (response.status === 'success' && response.data) {
        setAttendance(response.data);
        renderMap(response.data);
      } else {
        setAttendance(null);
        setError('No attendance record found for this date');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch attendance data');
      setAttendance(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeChange = (e) => {
    const employeeId = e.target.value;
    setSelectedEmployee(employeeId);
    if (employeeId) {
      fetchAttendance(employeeId, selectedDate);
    } else {
      setAttendance(null);
      clearMap();
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    if (selectedEmployee) {
      fetchAttendance(selectedEmployee, date);
    }
  };

  const handleRefresh = () => {
    if (selectedEmployee && selectedDate) {
      fetchAttendance(selectedEmployee, selectedDate);
    }
  };

  const clearMap = () => {
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Clear polyline
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
  };

  const renderMap = (attendanceData) => {
    if (!window.google || !window.google.maps || !mapRef.current) return;

    const locationLogs = attendanceData.locationLogs || [];
    
    if (locationLogs.length === 0) {
      setError('No location data available for this attendance record');
      return;
    }

    // Clear existing markers and polyline
    clearMap();

    // Initialize map if not already done
    if (!googleMapRef.current) {
      const firstLog = locationLogs[0];
      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: firstLog.latitude, lng: firstLog.longitude },
        zoom: 14,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
      });
    }

    // Prepare path for polyline
    const path = locationLogs
      .filter(log => log.latitude && log.longitude && !log.isMissed)
      .map(log => ({
        lat: log.latitude,
        lng: log.longitude
      }));

    if (path.length === 0) {
      setError('No valid location coordinates found');
      return;
    }

    // Draw polyline (route)
    polylineRef.current = new window.google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: '#4F46E5',
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map: googleMapRef.current
    });

    // Add markers for key points
    locationLogs.forEach((log, index) => {
      if (!log.latitude || !log.longitude || log.isMissed) return;

      let icon, title, color;
      
      if (log.type === 'punch_in') {
        icon = {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#10B981',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2
        };
        title = `Punch In: ${new Date(log.timestamp).toLocaleTimeString()}`;
        color = '#10B981';
      } else if (log.type === 'punch_out') {
        icon = {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#EF4444',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2
        };
        title = `Punch Out: ${new Date(log.timestamp).toLocaleTimeString()}`;
        color = '#EF4444';
      } else {
        icon = {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 5,
          fillColor: log.isWithinRange ? '#3B82F6' : '#F59E0B',
          fillOpacity: 0.8,
          strokeColor: '#FFFFFF',
          strokeWeight: 1
        };
        title = `Check: ${new Date(log.timestamp).toLocaleTimeString()}`;
        color = log.isWithinRange ? '#3B82F6' : '#F59E0B';
      }

      const marker = new window.google.maps.Marker({
        position: { lat: log.latitude, lng: log.longitude },
        map: googleMapRef.current,
        icon: icon,
        title: title
      });

      // Info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; font-weight: 600; color: ${color};">
              ${log.type === 'punch_in' ? '🟢 Punch In' : log.type === 'punch_out' ? '🔴 Punch Out' : '📍 Location Check'}
            </h3>
            <p style="margin: 4px 0; font-size: 14px;">
              <strong>Time:</strong> ${new Date(log.timestamp).toLocaleString()}
            </p>
            ${log.distanceFromOffice ? `
              <p style="margin: 4px 0; font-size: 14px;">
                <strong>Distance from Office:</strong> ${Math.round(log.distanceFromOffice)}m
              </p>
            ` : ''}
            <p style="margin: 4px 0; font-size: 14px;">
              <strong>Status:</strong> 
              <span style="color: ${log.isWithinRange ? '#10B981' : '#F59E0B'};">
                ${log.isWithinRange ? '✓ Within Range' : '⚠ Out of Range'}
              </span>
            </p>
            ${log.accuracy ? `
              <p style="margin: 4px 0; font-size: 14px;">
                <strong>Accuracy:</strong> ±${Math.round(log.accuracy)}m
              </p>
            ` : ''}
          </div>
        `
      });

      marker.addListener('click', () => {
        infoWindow.open(googleMapRef.current, marker);
      });

      markersRef.current.push(marker);
    });

    // Fit bounds to show all markers
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(point => bounds.extend(point));
    googleMapRef.current.fitBounds(bounds);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Navigation className="w-7 h-7 text-blue-600" />
          Employee Location Tracker
        </h1>
        <p className="text-gray-600 mt-1">
          Track employee location from punch-in to punch-out
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} onClose={() => setError('')} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Select Employee
            </label>
            <select
              value={selectedEmployee || ''}
              onChange={handleEmployeeChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Choose an employee...</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>
                  {emp.name || `${emp.firstName} ${emp.lastName}`} ({emp.employeeId})
                </option>
              ))}
            </select>
          </div>

          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Actions */}
          <div className="flex items-end gap-2">
            <button
              onClick={handleRefresh}
              disabled={!selectedEmployee || loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            {selectedDate === new Date().toISOString().split('T')[0] && (
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  autoRefresh
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                title={autoRefresh ? 'Auto-refresh enabled' : 'Enable auto-refresh'}
              >
                <Clock className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Attendance Summary */}
      {attendance && (
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Attendance Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 font-medium mb-1">Punch In</div>
              <div className="text-xl font-bold text-green-700">
                {formatTime(attendance.punchIn?.time)}
              </div>
            </div>
            
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-red-600 font-medium mb-1">Punch Out</div>
              <div className="text-xl font-bold text-red-700">
                {formatTime(attendance.punchOut?.time)}
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 font-medium mb-1">Work Duration</div>
              <div className="text-xl font-bold text-blue-700">
                {formatDuration(attendance.workDuration)}
              </div>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-purple-600 font-medium mb-1">Location Checks</div>
              <div className="text-xl font-bold text-purple-700">
                {attendance.locationLogs?.filter(log => !log.isMissed).length || 0}
              </div>
            </div>
          </div>

          {/* Violations */}
          {attendance.violations && attendance.violations.length > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="text-sm font-semibold text-yellow-800 mb-2">⚠️ Violations</h3>
              <ul className="space-y-1">
                {attendance.violations.map((violation, index) => (
                  <li key={index} className="text-sm text-yellow-700">
                    • {violation.description} ({new Date(violation.timestamp).toLocaleTimeString()})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Map Container */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Location Tracking Map
          </h2>
          <div className="mt-2 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-600">Punch In</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-gray-600">Punch Out</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="text-gray-600">Within Range</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
              <span className="text-gray-600">Out of Range</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <LoadingSpinner size="lg" />
          </div>
        ) : !selectedEmployee ? (
          <div className="flex flex-col items-center justify-center h-96 text-gray-500">
            <Navigation className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium">Select an employee to view location tracking</p>
            <p className="text-sm mt-1">Choose an employee and date from the filters above</p>
          </div>
        ) : !attendance ? (
          <div className="flex flex-col items-center justify-center h-96 text-gray-500">
            <MapPin className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium">No attendance data found</p>
            <p className="text-sm mt-1">The employee has not punched in for the selected date</p>
          </div>
        ) : (
          <div ref={mapRef} className="w-full h-96 md:h-[600px]"></div>
        )}
      </div>

      {/* Location Logs Timeline */}
      {attendance && attendance.locationLogs && attendance.locationLogs.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Location Timeline</h2>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {attendance.locationLogs
              .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
              .map((log, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    log.isMissed
                      ? 'bg-red-50 border border-red-200'
                      : log.type === 'punch_in'
                      ? 'bg-green-50 border border-green-200'
                      : log.type === 'punch_out'
                      ? 'bg-red-50 border border-red-200'
                      : log.isWithinRange
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-yellow-50 border border-yellow-200'
                  }`}
                >
                  <div className="flex-shrink-0 mt-1">
                    {log.isMissed ? (
                      <X className="w-5 h-5 text-red-600" />
                    ) : log.type === 'punch_in' ? (
                      <div className="w-5 h-5 rounded-full bg-green-500"></div>
                    ) : log.type === 'punch_out' ? (
                      <div className="w-5 h-5 rounded-full bg-red-500"></div>
                    ) : (
                      <MapPin className={`w-5 h-5 ${log.isWithinRange ? 'text-blue-600' : 'text-yellow-600'}`} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">
                        {log.isMissed
                          ? 'Missed Check'
                          : log.type === 'punch_in'
                          ? 'Punch In'
                          : log.type === 'punch_out'
                          ? 'Punch Out'
                          : 'Location Check'}
                      </span>
                      <span className="text-sm text-gray-600">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    {!log.isMissed && (
                      <div className="mt-1 text-sm text-gray-600">
                        <div>
                          Coordinates: {log.latitude?.toFixed(6)}, {log.longitude?.toFixed(6)}
                        </div>
                        {log.distanceFromOffice && (
                          <div>Distance from office: {Math.round(log.distanceFromOffice)}m</div>
                        )}
                        {log.accuracy && (
                          <div>Accuracy: ±{Math.round(log.accuracy)}m</div>
                        )}
                      </div>
                    )}
                    {log.failureReason && (
                      <div className="mt-1 text-sm text-red-600">
                        Reason: {log.failureReason}
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeLocationTracker;
